import { useCallback, useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { getServerUrl } from "../../utils/fetchConfigClient"
import "./bilibili-streams.css"

type BilibiliStream = {
  mid: string
  name: string
  roomId: number
  title: string
  cover: string
  url: string
  online: number
}

function formatPopularity(count: number): string {
  if (count >= 1000) {
    return `${(count / 1000).toFixed(1).replace(/\.0$/, "")}k`
  }
  return String(count)
}

type BilibiliStreamsResponse = {
  streams: BilibiliStream[]
  error: string | null
}

export function BilibiliStreams() {
  const { t } = useTranslation()
  const [streams, setStreams] = useState<BilibiliStream[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetchStreams = useCallback(async () => {
    try {
      const response = await fetch(getServerUrl("/bilibili/streams"))
      const data = (await response.json()) as BilibiliStreamsResponse
      setStreams(data.streams ?? [])
      setError(data.error)
    } catch {
      setError(t("bilibili_streams.unavailable"))
    } finally {
      setIsLoading(false)
    }
  }, [t])

  useEffect(() => {
    let isMounted = true

    fetchStreams().catch(() => undefined)
    const interval = setInterval(
      () => {
        if (isMounted) {
          fetchStreams().catch(() => undefined)
        }
      },
      1000 * 60 * 1
    )

    return () => {
      isMounted = false
      clearInterval(interval)
    }
  }, [fetchStreams])

  if (isLoading) {
    return <p className="subtitle">{t("loading")}</p>
  }

  if (error && streams.length === 0) {
    return <p className="subtitle">{error}</p>
  }

  return (
    <div className="bilibili-streams">
      {streams.map((stream) => (
        <a
          className="bilibili-stream-card my-box"
          key={stream.roomId}
          href={stream.url}
          rel="noreferrer"
          target="_blank"
        >
          <img
            alt={stream.title}
            className="bilibili-stream-thumbnail"
            loading="lazy"
            referrerPolicy="no-referrer"
            src={stream.cover}
          />
          <div className="bilibili-stream-content">
            <h3>{stream.name}</h3>
            <p title={stream.title}>{stream.title}</p>
            <div className="bilibili-stream-meta">
              <span className="bilibili-stream-viewers">
                {t("bilibili_streams.popularity", {
                  count: formatPopularity(stream.online)
                })}
              </span>
            </div>
          </div>
        </a>
      ))}
    </div>
  )
}
